import React from "react";
import { useLang } from "@/lib/i18n";
import { Kicker, SectionTitle, Reveal } from "./Section";

const A = "https://customer-assets.emergentagent.com/job_stadt-zukunft-heute/artifacts/";

// Vollständiges Bildarchiv — im Projekt behalten, aber nicht mehr auf der Landing gerendert.
// Straffung 18.07.2026: nur 6 stärkste Impressionen (eine pro Jahreszeit + zwei Highlights).
const GALLERY_ALL = [
  { url: `${A}o9h3k8ew_grafik.png`, de: "Sommer · Grüner Pergola-Cube mit Lounge", en: "Summer · Green pergola cube with lounge" },
  { url: `${A}ycq1xlju_grafik.png`, de: "Green Cube · Pavillon mit vertikalem Garten", en: "Green Cube · Pavilion with vertical garden" },
  { url: `${A}dic8bwnw_grafik.png`, de: "Media Cube · Wasserfall-Display im Baumdach", en: "Media Cube · Waterfall display in tree canopy" },
  { url: `${A}49iizw9u_grafik.png`, de: "Lounge Cube · Vertikaler Garten am Museum", en: "Lounge Cube · Vertical garden at the museum" },
  { url: `${A}ai565hr3_grafik.png`, de: "Green Cube · Golden Hour am Berliner Dom", en: "Green Cube · Golden hour at Berlin Cathedral" },
  { url: `${A}1a4t87rn_grafik.png`, de: "Lounge Cube · Sonnenuntergang mit Lavendel", en: "Lounge Cube · Sunset with lavender" },
  { url: `${A}l6oa67br_grafik.png`, de: "Lounge Cube · Paar am Berliner Dom", en: "Lounge Cube · Couple at Berlin Cathedral" },
  { url: `${A}xfdgznb7_grafik.png`, de: "Smart Cube · Berlin Wayfinding & Fernsehturm", en: "Smart Cube · Berlin wayfinding & TV Tower" },
  { url: `${A}78890o30_grafik.png`, de: "Mobility Cube · E-Bike Ladestation", en: "Mobility Cube · E-bike charging station" },
  { url: `${A}npa0fhck_grafik.png`, de: "Art Cube · Berliner Fernsehturm bei Dämmerung", en: "Art Cube · Berlin TV Tower at dusk" },
  { url: `${A}pn5a8q3f_grafik.png`, de: "Art Cube · Skyline Golden Hour", en: "Art Cube · Skyline Golden Hour" },
  { url: `${A}nlttjni7_grafik.png`, de: "Art Cube · Kuratierte Kunst im Straßenraum", en: "Art Cube · Curated art on the street" },
  { url: `${A}d74qunkd_grafik.png`, de: "Waste Cube · Recycling mit LED-Kennzeichnung", en: "Waste Cube · Recycling with LED signage" },
  { url: `${A}t58u48d0_grafik.png`, de: "Winter · Schneefall am Berliner Dom", en: "Winter · Snowfall at Berlin Cathedral" },
  { url: `${A}a1wpyvxk_grafik.png`, de: "Weihnachtsmarkt · Media Cube im Schnee", en: "Christmas market · Media Cube in the snow" },
  { url: `${A}dkrsnlhf_grafik.png`, de: "Winter · Sitzmodule im Schnee mit warmer LED", en: "Winter · Seat modules in snow with warm LED" },
  { url: `${A}rd1hq9er_grafik.png`, de: "Silvester · Feuerwerk über der Stadt", en: "New Year's Eve · Fireworks over the city" },
  { url: `${A}417ozbig_grafik.png`, de: "Silvester · Neujahr am Boulevard", en: "New Year's Eve · Fireworks on the boulevard" },
];

// 6 Highlights — Frühling, Sommer, Herbst/Golden Hour, Winter, Weihnachten, Silvester.
const GALLERY = [
  { url: `${A}ycq1xlju_grafik.png`, de: "Frühling · Green Cube mit vertikalem Garten", en: "Spring · Green Cube with vertical garden" },
  { url: `${A}o9h3k8ew_grafik.png`, de: "Sommer · Grüner Pergola-Cube mit Lounge", en: "Summer · Green pergola cube with lounge" },
  { url: `${A}b5kedaty_grafik.png`, de: "Herbst · Golden Hour am Berliner Dom", en: "Autumn · Golden hour at Berlin Cathedral" },
  { url: `${A}t58u48d0_grafik.png`, de: "Winter · Schneefall am Berliner Dom", en: "Winter · Snowfall at Berlin Cathedral" },
  { url: `${A}a1wpyvxk_grafik.png`, de: "Highlight · Weihnachtsmarkt im Schnee", en: "Highlight · Christmas market in the snow" },
  { url: `${A}rd1hq9er_grafik.png`, de: "Highlight · Silvester über der Stadt", en: "Highlight · New Year's Eve over the city" },
];

export default function Gallery() {
  const { lang } = useLang();
  const copy = {
    de: {
      kicker: "05 — Ganzjährig",
      title: "Vier Jahreszeiten. Ein System.",
      body:
        "Von Sommernacht bis Silvester, vom Golden Hour am Dom bis zum Schneefall — der Berlin-Cube bleibt Teil des Stadtlebens.",
    },
    en: {
      kicker: "05 — All seasons",
      title: "Four seasons. One system.",
      body:
        "From summer nights to New Year's Eve, golden hour to snowfall — Berlin-Cube stays part of city life.",
    },
  }[lang];

  return (
    <section
      id="gallery"
      data-testid="section-gallery"
      className="relative bg-[#16191D] py-24 md:py-32 overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-14">
        <div className="grid lg:grid-cols-12 gap-8 mb-10">
          <div className="lg:col-span-7">
            <Reveal>
              <Kicker>{copy.kicker}</Kicker>
              <SectionTitle>{copy.title}</SectionTitle>
            </Reveal>
          </div>
          <div className="lg:col-span-4 lg:col-start-9 flex items-end">
            <Reveal delay={0.1}>
              <p className="text-white/55 text-base leading-relaxed">{copy.body}</p>
            </Reveal>
          </div>
        </div>
      </div>

      <Reveal delay={0.15}>
        <div className="pl-6 md:pl-14 overflow-x-auto no-scrollbar" style={{ scrollbarWidth: "none" }}>
          <div className="flex gap-5 pr-6 md:pr-14 min-w-max">
            {GALLERY.map((it, i) => (
              <a
                key={i}
                href="#cta"
                data-testid={`gallery-item-${i}`}
                className="group relative shrink-0 w-[320px] md:w-[440px] border border-white/8 hover:border-[#B18B5E]/60 transition-colors overflow-hidden bg-[#1F2328]"
              >
                <div className="relative aspect-[4/5] overflow-hidden">
                  <img
                    src={it.url}
                    alt={it[lang]}
                    className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1F2328] via-[#1F2328]/20 to-transparent" />
                  <div className="absolute top-4 left-4 text-[10px] tracking-[0.3em] uppercase text-white/70">
                    Impression · {String(i + 1).padStart(2, "0")}
                  </div>
                </div>
                <div className="p-5 flex items-center justify-between">
                  <div className="font-display text-[15px] text-white/85 tracking-tight">
                    {it[lang]}
                  </div>
                </div>
              </a>
            ))}
            <div className="shrink-0 w-2" />
          </div>
        </div>
      </Reveal>
    </section>
  );
}
